import { cn } from '@workspace/ui/lib/utils'
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'

export interface StatCellProps {
  value: ReactNode
  label: string
  sub?: string
  /** Tints the cell and the number in the brand colour. */
  accent?: boolean
  /** Small emphasis pill in the top-right corner (e.g. "First month free"). */
  badge?: string
  /**
   * Makes the whole cell activatable. The label grows a trailing arrow so the
   * affordance is visible without a separate link.
   */
  onClick?: () => void
  /**
   * Shows the arrow affordance without owning the interaction — for cells the
   * caller has wrapped in a router link. Routing stays in the app; this package
   * only knows the cell should look clickable.
   *
   * Wrap with `className="group block"` so the arrow's hover nudge still fires.
   */
  interactive?: boolean
  /**
   * `md` is the hero row; `sm` is the denser funnel grid, where six cells share
   * the width three would otherwise take.
   */
  size?: 'sm' | 'md'
  className?: string
}

/**
 * A single number in a stat row: big figure, label, supporting line.
 *
 * Existed three times before this — twice as `HeroStat` (the acquisition hero
 * and the active outreach view, the second a superset of the first) and once as
 * `SummaryStatCell` on the dashboard. This is the superset.
 */
export function StatCell({
  value,
  label,
  sub,
  accent,
  badge,
  onClick,
  interactive,
  size = 'md',
  className,
}: StatCellProps) {
  const showsArrow = Boolean(onClick || interactive)
  const isSmall = size === 'sm'
  const padding = isSmall ? 'px-4 py-4' : 'px-5 py-5'
  const body = (
    <>
      {badge && (
        <span className="absolute right-3 top-3 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-emerald-400">
          {badge}
        </span>
      )}
      <div
        className={cn(
          'font-bold leading-none tracking-[-0.02em] tabular-nums',
          isSmall ? 'text-[24px]' : 'text-[40px]',
          accent ? 'text-primary' : 'text-foreground'
        )}
      >
        {value}
      </div>
      <div
        className={cn(
          'mt-1 flex items-center gap-1 font-semibold text-foreground',
          isSmall ? 'text-[12px]' : 'text-[13px]'
        )}
      >
        {label}
        {showsArrow && (
          <ArrowRight className="size-3.5 text-primary transition-transform group-hover:translate-x-0.5" />
        )}
      </div>
      {sub && (
        <p
          className={cn(
            'leading-relaxed text-muted-foreground',
            isSmall ? 'mt-0.5 text-[10.5px]' : 'mt-1 text-[11.5px]'
          )}
        >
          {sub}
        </p>
      )}
    </>
  )

  if (onClick) {
    return (
      // Deliberately a div rather than a native <button>: UA button styling
      // nudges the value larger and lower than its neighbours, so a row of
      // mixed static and clickable cells stops sharing a baseline.
      // biome-ignore lint/a11y/useSemanticElements: keeps font metrics identical to the static cells beside it.
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          onClick()
        }}
        className={cn(
          'group relative block w-full cursor-pointer bg-background text-left transition-colors hover:bg-foreground/[0.03]',
          padding,
          accent && 'bg-primary/5',
          className
        )}
      >
        {body}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative bg-background',
        padding,
        // The caller owns the link, so it also owns the hover surface.
        interactive && 'transition-colors group-hover:bg-foreground/[0.03]',
        accent && 'bg-primary/5',
        className
      )}
    >
      {body}
    </div>
  )
}
